import { Link, useParams } from "react-router-dom";
import Layout from "@/components/feature/Layout";
import Breadcrumb from "@/components/feature/Breadcrumb";
import VideoCard from "@/components/feature/VideoCard";
import { categoryMap } from "@/mocks/categories";
import { useDocumentMeta } from "@/hooks/useDocumentMeta";
import {
  formatViews,
  getRelatedVideos,
  getTypeName,
  getVideoById,
} from "@/utils/video";

export default function Detail() {
  const { id = "" } = useParams();
  const video = getVideoById(id);

  useDocumentMeta(
    video ? `${video.title} - 影视详情 - 污视频` : "影视详情 - 污视频",
    video
      ? `查看${video.title}的详细介绍与相关视频推荐，尽在污视频。`
      : undefined,
  );

  if (!video) {
    return (
      <Layout>
        <div className="flex flex-col items-center justify-center px-4 py-24 text-center">
          <span className="flex h-20 w-20 items-center justify-center rounded-full bg-background-200 text-foreground-400">
            <i className="ri-file-unknow-line text-4xl"></i>
          </span>
          <h1 className="mt-6 font-heading text-2xl font-bold text-foreground-950">
            视频不存在
          </h1>
          <p className="mt-2 text-sm text-foreground-500">
            你要查看的资源可能已被移除，返回首页继续浏览吧。
          </p>
          <Link
            to="/"
            className="mt-6 inline-flex items-center gap-2 whitespace-nowrap rounded-md bg-accent-500 px-5 py-2.5 text-sm font-semibold text-background-50 transition-colors hover:bg-accent-600"
          >
            <i className="ri-home-4-line"></i>
            返回首页
          </Link>
        </div>
      </Layout>
    );
  }

  const category = categoryMap[video.type];
  const related = getRelatedVideos(video, 5);

  const metaList = [
    { icon: "ri-movie-line", label: "类型", value: getTypeName(video.type) },
    { icon: "ri-user-star-line", label: "导演", value: video.director },
    { icon: "ri-group-line", label: "主演", value: video.actors.join(" / ") },
    { icon: "ri-earth-line", label: "地区", value: video.region },
    { icon: "ri-calendar-line", label: "年份", value: String(video.year) },
    { icon: "ri-refresh-line", label: "更新日期", value: video.updateDate },
  ];

  return (
    <Layout>
      <div className="w-full px-4 pb-10 pt-6 md:px-8 md:pt-8">
        <Breadcrumb
          items={[
            { label: "首页", to: "/" },
            { label: category.name, to: `/category/${video.type}` },
            { label: video.title },
          ]}
        />

        <div className="mt-6 grid grid-cols-1 gap-6 lg:grid-cols-3 lg:gap-8">
          <div className="lg:col-span-2">
            <div className="flex flex-wrap items-center gap-3">
              <span className="rounded-md bg-primary-500 px-2.5 py-1 text-xs font-medium text-background-50">
                {getTypeName(video.type)}
              </span>
              <span className="flex items-center gap-1 rounded-md bg-accent-500 px-2.5 py-1 text-xs font-semibold text-background-50">
                <i className="ri-star-fill"></i>
                {video.rating.toFixed(1)} 分
              </span>
              <span className="flex items-center gap-1 rounded-md bg-secondary-100 px-2.5 py-1 text-xs font-medium text-secondary-800">
                <i className="ri-fire-line"></i>
                {formatViews(video.views)} 次浏览
              </span>
            </div>

            <h1 className="mt-4 font-heading text-2xl font-bold text-foreground-950 md:text-4xl">
              {video.title}
            </h1>

            <div className="mt-5 w-full overflow-hidden rounded-lg border border-background-200 bg-background-50">
              <div className="aspect-video w-full">
                <img
                  src={video.cover}
                  alt={`${video.title}封面大图`}
                  title={`${video.title} - 污视频影视详情`}
                  className="h-full w-full object-cover object-top"
                />
              </div>
            </div>

            <div className="mt-6 flex flex-wrap gap-2">
              {video.tags.map((tag) => (
                <Link
                  key={tag}
                  to={`/category/${video.type}`}
                  className="whitespace-nowrap rounded-full bg-background-100 px-3.5 py-1.5 text-sm text-foreground-600 transition-colors hover:bg-accent-100 hover:text-accent-700"
                >
                  #{tag}
                </Link>
              ))}
            </div>

            <div className="mt-6 rounded-lg border border-background-200 bg-background-50 p-5 md:p-6">
              <h2 className="flex items-center gap-2 font-heading text-lg font-bold text-foreground-950">
                <i className="ri-file-text-line text-accent-500"></i>
                剧情简介
              </h2>
              <p className="mt-3 text-sm leading-relaxed text-foreground-600 md:text-base">
                {video.description}
              </p>
              <p className="mt-4 rounded-md bg-accent-50 px-4 py-3 text-xs text-accent-700">
                <i className="ri-information-line mr-1"></i>
                本站仅提供视频资源信息展示，不提供播放与下载功能，以上内容为演示数据。
              </p>
            </div>
          </div>

          <div className="lg:col-span-1">
            <div className="sticky top-24 rounded-lg border border-background-200 bg-background-50">
              <div className="border-b border-background-100 px-5 py-4">
                <h2 className="font-heading text-base font-bold text-foreground-950">
                  视频信息
                </h2>
              </div>
              <ul className="divide-y divide-background-100">
                {metaList.map((item) => (
                  <li
                    key={item.label}
                    className="flex items-start gap-3 px-5 py-3"
                  >
                    <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md bg-background-100 text-secondary-600">
                      <i className={`${item.icon} text-base`}></i>
                    </span>
                    <div className="min-w-0">
                      <p className="text-xs text-foreground-400">
                        {item.label}
                      </p>
                      <p className="mt-0.5 text-sm font-medium text-foreground-800">
                        {item.value}
                      </p>
                    </div>
                  </li>
                ))}
              </ul>
              <div className="px-5 py-4">
                <Link
                  to={`/category/${video.type}`}
                  className="flex w-full items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600"
                >
                  <i className="ri-arrow-left-line"></i>
                  返回{category.name}分类
                </Link>
              </div>
            </div>
          </div>
        </div>

        <div className="mt-10 md:mt-14">
          <div className="mb-6 flex items-end justify-between">
            <div>
              <h2 className="flex items-center gap-2 font-heading text-xl font-bold text-foreground-950 md:text-2xl">
                <i className="ri-magic-line text-accent-500"></i>
                相关推荐
              </h2>
              <p className="mt-1 text-sm text-foreground-500">
                根据类型与标签为你推荐相近的作品
              </p>
            </div>
            <Link
              to={`/category/${video.type}`}
              className="hidden items-center gap-1 whitespace-nowrap text-sm font-medium text-accent-600 transition-colors hover:text-accent-700 sm:flex"
            >
              查看更多
              <i className="ri-arrow-right-line"></i>
            </Link>
          </div>
          <div className="grid grid-cols-2 gap-3 md:gap-4 lg:grid-cols-4">
            {related.map((item) => (
              <VideoCard key={item.id} video={item} />
            ))}
          </div>
        </div>
      </div>
    </Layout>
  );
}